import { useState, useEffect } from "react";

const css = `
  .install-banner {
    position: fixed;
    bottom: 80px;
    left: 12px;
    right: 12px;
    z-index: 999;
    background: var(--card);
    border: 1px solid var(--blue);
    border-radius: 16px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    box-shadow: 0 8px 32px rgba(0,0,0,.5), 0 0 20px rgba(59,130,246,.15);
    animation: slideUp .3s ease;
  }
  .install-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #080b10, #1a2030);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--head);
    font-size: 11px;
    font-weight: 800;
    color: var(--blue);
    letter-spacing: 1px;
    flex-shrink: 0;
    line-height: 1.2;
    text-align: center;
  }
  .install-text { flex: 1; min-width: 0; }
  .install-title { font-weight: 700; font-size: 14px; margin-bottom: 2px; }
  .install-sub { font-size: 12px; color: var(--muted); }
  .install-actions { display: flex; gap: 8px; flex-shrink: 0; }
  .install-btn-add {
    background: var(--blue); color: #fff; border: none;
    border-radius: 8px; padding: 8px 14px;
    font-family: var(--body); font-size: 13px; font-weight: 600;
    cursor: pointer; white-space: nowrap;
  }
  .install-btn-dismiss {
    background: transparent; color: var(--muted);
    border: 1px solid var(--border); border-radius: 8px;
    padding: 8px 10px; font-family: var(--body);
    font-size: 13px; cursor: pointer;
  }

  /* iOS instruction banner (no install API on Safari) */
  .ios-banner {
    position: fixed;
    bottom: 80px;
    left: 12px;
    right: 12px;
    z-index: 999;
    background: var(--card);
    border: 1px solid var(--blue);
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 8px 32px rgba(0,0,0,.5);
    animation: slideUp .3s ease;
  }
  .ios-banner-header {
    display: flex; align-items: center;
    justify-content: space-between; margin-bottom: 12px;
  }
  .ios-banner-title { font-weight: 700; font-size: 14px; }
  .ios-steps { display: flex; flex-direction: column; gap: 8px; }
  .ios-step { display: flex; align-items: center; gap: 10px; font-size: 13px; color: var(--muted); }
  .ios-step-num {
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--blue); color: #fff;
    font-size: 11px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  /* Arrow pointing down to Safari toolbar */
  .ios-arrow {
    position: absolute; bottom: -8px; left: 50%;
    transform: translateX(-50%);
    width: 0; height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid var(--blue);
  }

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
`;

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isInStandaloneMode() {
  return window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid, setShowAndroid]       = useState(false);
  const [showIOS, setShowIOS]               = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isInStandaloneMode()) return;

    // Don't show if dismissed in last 7 days
    const lastDismissed = localStorage.getItem("pwa_dismissed");
    if (lastDismissed && Date.now() - parseInt(lastDismissed) < 7 * 24 * 60 * 60 * 1000) return;

    if (isIOS()) {
      // Show iOS instructions after 3 seconds
      const t = setTimeout(() => setShowIOS(true), 3000);
      return () => clearTimeout(t);
    }

    // Android / Chrome — listen for beforeinstallprompt
    const handler = e => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowAndroid(true), 3000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowAndroid(false);
    if (outcome === "dismissed") {
      localStorage.setItem("pwa_dismissed", Date.now().toString());
    }
  };

  const handleDismiss = () => {
    setShowAndroid(false);
    setShowIOS(false);
    localStorage.setItem("pwa_dismissed", Date.now().toString());
  };

  if (!showAndroid && !showIOS) return null;

  return (
    <>
      <style>{css}</style>

      {showAndroid && (
        <div className="install-banner">
          <div className="install-icon">NEO<br/>DETAIL</div>
          <div className="install-text">
            <div className="install-title">Add to Home Screen</div>
            <div className="install-sub">Install the app for quick access</div>
          </div>
          <div className="install-actions">
            <button className="install-btn-dismiss" onClick={handleDismiss}>✕</button>
            <button className="install-btn-add" onClick={handleInstall}>Install</button>
          </div>
        </div>
      )}

      {showIOS && (
        <div className="ios-banner">
          <div className="ios-banner-header">
            <div className="ios-banner-title">📲 Add NEO Detail to Home Screen</div>
            <button className="install-btn-dismiss" onClick={handleDismiss}
              style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"4px 8px", color:"var(--muted)", cursor:"pointer", fontSize:13 }}>
              ✕
            </button>
          </div>
          <div className="ios-steps">
            <div className="ios-step">
              <div className="ios-step-num">1</div>
              <span>Tap the <strong style={{color:"var(--text)"}}>Share</strong> button at the bottom of Safari</span>
            </div>
            <div className="ios-step">
              <div className="ios-step-num">2</div>
              <span>Scroll down and tap <strong style={{color:"var(--text)"}}>Add to Home Screen</strong></span>
            </div>
            <div className="ios-step">
              <div className="ios-step-num">3</div>
              <span>Tap <strong style={{color:"var(--text)"}}>Add</strong> — it'll appear on your home screen like an app</span>
            </div>
          </div>
          <div className="ios-arrow" />
        </div>
      )}
    </>
  );
}
